import React, { useEffect, useState } from "react";
import { Modal, Box, Tooltip } from "@mui/material";
import EllipsisText from "./Ellipsis";
import { PDFSVG } from "../../assets/images/images";
import { CrossIcon, DownloadIcon } from "../FileRepo/Icons";
import { downloadFile } from "../FileRepo/utils/utils";
import { toast } from "react-toastify";

export interface FileType {
  name: string;
  download_url?: string;
  preview_url?: string;
  size: number;
  extension?: string;
  is_dir?: boolean;
  s3_url?: string;
}

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FileType | null;
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "900px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1000001, // Higher than chatbot drawer (999999) and expanded drawer (1000000)
};

const FilePreviewModal2: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  file,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txtContent, setTxtContent] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && file) {
      setIsLoading(true);
      setError(null);
      setTxtContent(null);

      if (file.preview_url) {
        setPreviewUrl(file.preview_url);
        setIsLoading(false);
      } else if (file.download_url) {
        setPreviewUrl(file.download_url);
        setIsLoading(false);
      } else {
        setError("No preview available for this file");
        setIsLoading(false);
      }

      if (
        file.extension?.toLowerCase() === "txt" &&
        (file.preview_url || file.download_url)
      ) {
        const urlToFetch = file.preview_url || file.download_url;

        if (urlToFetch) {
          fetch(urlToFetch)
            .then((res) => {
              if (!res.ok) throw new Error();
              return res.text();
            })
            .then((data) => {
              setTxtContent(data);
              setIsLoading(false);
            })
            .catch(() => {
              setError("Unable to load the text file.");
              setIsLoading(false);
            });
        }
      }
    }
  }, [isOpen, file]);

  const handleClose = () => {
    setPreviewUrl(null);
    setError(null);
    setTxtContent(null);
    onClose();
  };

  const handleDownload = () => {
    if (file) {
      if (file.s3_url) {
        downloadFile(file?.s3_url, file?.name);
      } else if (file?.download_url) {
        downloadFile(file?.download_url, file?.name);
      } else {
        return toast.error("No download available for this file");
      }
    }
  };

  if (!file) return null;

  const ext = file.extension?.toLowerCase() || "";

  const getFileIcon = () => {
    if (ext === "pdf")
      return <img src={PDFSVG} alt="PDF" className="w-6 h-6" />;
    return <span>📄</span>;
  };

  const canPreview = [
    "pdf",
    "doc",
    "docx",
    "xlsx",
    "xls",
    "jpg",
    "jpeg",
    "png",
    "gif",
    "txt",
    "pptx",
  ].includes(ext);

  return (
    <Modal open={isOpen} onClose={handleClose} sx={{ zIndex: 1000001 }}>
      <Box sx={modalStyle}>
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center space-x-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                <EllipsisText text={file.name} length={50} />
              </h2>
            </div>
          </div>
          <div className="flex gap-2 align-center">
            <Tooltip title="Download">
              <button
                onClick={handleDownload}
                className="border-none bg-transparent rounded-md "
              >
                <DownloadIcon w={"16"} h={"16"} />
              </button>
            </Tooltip>

            <Tooltip title="Close">
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 bg-transparent border-none"
              >
                <CrossIcon />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="text-6xl mb-4">{getFileIcon()}</div>
              <h3 className="text-lg font-semibold mb-2">
                Preview Not Available
              </h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={handleDownload}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Download File
              </button>
            </div>
          )}

          {previewUrl && canPreview && (
            <div className="w-full h-full">
              {ext === "pdf" ? (
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    previewUrl,
                  )}&embedded=true`}
                  className="w-full h-full min-h-[500px] border-0"
                  title={file.name}
                />
              ) : ["doc", "docx", "xlsx", "xls", "pptx"].includes(ext) ? (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    previewUrl,
                  )}`}
                  className="w-full h-full min-h-[500px] border-0"
                  title={file.name}
                />
              ) : ext === "txt" ? (
                <div className="p-4 bg-gray-100 border rounded-md">
                  <pre className="whitespace-pre-wrap">{txtContent}</pre>
                </div>
              ) : (
                <img
                  src={previewUrl}
                  alt={file.name}
                  className="max-w-full max-h-full mx-auto"
                />
              )}
            </div>
          )}

          {previewUrl && !canPreview && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <h3 className="text-lg font-semibold mb-2">File Preview</h3>
              <p className="text-gray-500 mb-4">
                This file type cannot be previewed.
              </p>
              <button
                onClick={handleDownload}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Download File
              </button>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default FilePreviewModal2;
